import React from 'react';
import { toast } from 'sonner';
import { History, RotateCcw, CalendarClock, X, BarChart3, Bookmark, Trash2, Lock } from 'lucide-react';
import { api, useApi, when } from '../../api';
import { usePrivacy } from '../../privacy';
import { undoable } from '../../undo';
import { Skel } from '../../ui';
import { PlanLock, showUpgrade } from '../../Upgrade';

const Box = ({ icon: Icon, title, children, testid, loading }) => (
  <div className="civr-card p-6" data-testid={testid} aria-busy={loading || undefined}>
    <div className="flex items-center gap-2 font-display text-xl font-bold mb-4"><Icon className="w-4 h-4 text-[#818cf8]" />{title}</div>
    {loading ? <div className="flex flex-col gap-3"><Skel className="h-3.5 w-3/4" /><Skel className="h-3.5 w-1/2" /><Skel className="h-3.5 w-2/3" /></div> : children}
  </div>
);
const none = (t) => <div className="text-sm text-[#a1a1aa]">{t}</div>;

export function RecentCalls({ callers, onDone, stamp }) {
  const { data, reload } = useApi(`/calls/history?s=${stamp}`);
  const pv = usePrivacy();
  const redial = async (id) => {
    try { const d = await api(`/calls/history/${id}/redial`, { method: 'POST' }); toast.success(d.dryRun ? d.message : `Calling again · ${d.started} call(s)`); reload(); onDone(); } catch (e) { toast.error(e.message); }
  };
  const list = data?.calls || [];
  const btn = (c, i) => {
    const n = !c.trial && callers.find(x => x.phoneNumber === c.callerId);
    if (!n) return null;
    if (n.access?.ivrRedial) return <button type="button" className="civr-btn civr-btn-ghost civr-btn-sm" onClick={() => redial(c.id)} data-testid={`quick-history-redial-${i}`}><RotateCcw className="w-3.5 h-3.5" />Redial</button>;
    return <button type="button" className="civr-btn civr-btn-ghost civr-btn-sm opacity-70" onClick={() => showUpgrade({ feature: 'Redial', need: 'business', phone: n.phoneNumber })} data-testid={`quick-history-redial-locked-${i}`}><Lock className="w-3.5 h-3.5 text-[#fbbf24]" />Redial</button>;
  };
  return (
    <Box icon={History} title="Recent calls" testid="quick-history" loading={!data}>
      {!list.length ? none('Your Quick IVR calls show up here.') : list.slice(0, 6).map((c, i) => (
        <div key={c.id} className="flex items-center gap-2 py-2 border-b border-[#1f1f1f] last:border-0" data-testid={`quick-history-row-${i}`}>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs truncate">{c.targets.map(t => pv.phone(t)).join(', ')}</div>
            <div className="text-xs text-[#a1a1aa]">{when(c.at)} · {c.mode === 'otp' ? 'Code' : c.hasMenu ? 'Menu' : 'Transfer'}{c.dryRun ? ' · preview' : ''}{c.trial ? ' · free trial' : ''}</div>
          </div>
          {btn(c, i)}
        </div>
      ))}
    </Box>
  );
}

export function ScheduledCalls({ stamp }) {
  const { data, reload, setData } = useApi(`/calls/scheduled?s=${stamp}`);
  const pv = usePrivacy();
  const cancel = (id) => undoable('Scheduled call cancelled', {
    hide: () => setData(d => ({ ...d, calls: (d?.calls || []).filter(x => x.id !== id) })),
    restore: reload,
    run: async () => { await api(`/calls/scheduled/${id}`, { method: 'DELETE' }); reload(); },
  });
  const list = (data?.calls || []).slice(0, 6);
  if (!list.length) return null;
  return (
    <Box icon={CalendarClock} title="Scheduled" testid="quick-scheduled">
      {list.map((c, i) => (
        <div key={c.id} className="flex items-center gap-2 py-2 border-b border-[#1f1f1f] last:border-0" data-testid={`quick-scheduled-row-${i}`}>
          <div className="flex-1 min-w-0"><div className="font-mono text-xs">{pv.phone(c.to)}</div><div className="text-xs text-[#a1a1aa]">{when(c.scheduledFor)} · {c.status}</div></div>
          {['pending', 'preview'].includes(c.status) && <button type="button" className="p-2 text-[#a1a1aa] hover:text-[#fb7185]" onClick={() => cancel(c.id)} aria-label="Cancel scheduled call" data-testid={`quick-scheduled-cancel-${i}`}><X className="w-4 h-4" /></button>}
        </div>
      ))}
    </Box>
  );
}

export function MenuStats() {
  const { data } = useApi('/calls/menu-stats');
  const rows = data?.keyBreakdown || [];
  return (
    <Box icon={BarChart3} title="Key presses · 30 days" testid="quick-menu-stats" loading={!data}>
      {!rows.length ? none('No key presses yet.') : rows.map((k, i) => (
        <div key={i} className="mb-2" data-testid={`quick-menu-stats-row-${i}`}>
          <div className="flex justify-between text-sm"><span>Key {k.digit} · {k.action}</span><span className="font-mono">{k.count} · {k.percent}%</span></div>
          <div className="h-1.5 bg-[#262626] rounded-full mt-1"><div className="h-full bg-[#818cf8] rounded-full" style={{ width: `${k.percent}%` }} /></div>
        </div>
      ))}
    </Box>
  );
}

export function Presets({ onLoad, current, stamp, locked, phone }) {
  const { data, reload, setData } = useApi(`/calls/presets?s=${stamp}`);
  const [name, setName] = React.useState('');
  const upsell = () => showUpgrade({ feature: 'Saved call presets', need: 'business', phone });
  const save = async () => {
    try { await api('/calls/presets', { method: 'POST', body: { name, setup: current() } }); toast.success('Preset saved'); setName(''); reload(); } catch (e) { toast.error(e.message); }
  };
  const del = (id) => undoable('Preset deleted', {
    hide: () => setData(d => ({ ...d, presets: (d?.presets || []).filter(x => x.id !== id) })),
    restore: reload,
    run: async () => { await api(`/calls/presets/${id}`, { method: 'DELETE' }); reload(); },
  });
  const list = data?.presets || [];
  return (
    <Box icon={Bookmark} title={<span className="flex flex-wrap items-center gap-2">Presets{locked && <PlanLock feature="Saved call presets" phone={phone} testid="quick-presets-lock" />}</span>} testid="quick-presets">
      {list.map((p, i) => (
        <div key={p.id} className="flex items-center gap-2 py-1.5" data-testid={`quick-preset-row-${i}`}>
          <button type="button" className={`flex-1 text-left text-sm truncate ${locked ? 'text-[#71717a]' : 'hover:text-[#818cf8]'}`} onClick={() => (locked ? upsell() : onLoad(p))} data-testid={`quick-preset-load-${i}`}>{p.name}</button>
          <button type="button" className="p-1.5 text-[#a1a1aa] hover:text-[#fb7185]" onClick={() => del(p.id)} aria-label="Delete preset" data-testid={`quick-preset-delete-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      {locked ? <button type="button" onClick={upsell} className="w-full text-left text-xs text-[#a1a1aa] border border-dashed border-[#fbbf24]/30 rounded-xl p-3 hover:border-[#fbbf24]/60 transition-colors" data-testid="quick-presets-locked">Save a whole call setup and reuse it in one tap — on the Business plan.</button> : <>
        <div className="flex gap-2 mt-2"><input className="civr-input !h-10" value={name} onChange={e => setName(e.target.value)} placeholder="Preset name" data-testid="quick-preset-name-input" />
          <button type="button" className="civr-btn civr-btn-ghost civr-btn-sm" disabled={!name.trim()} onClick={save} data-testid="quick-preset-save-button">Save</button></div>
        <p className="text-xs text-[#a1a1aa] mt-2">Saves this setup — everything except the numbers to call.</p>
      </>}
    </Box>
  );
}
